// The plan canvas (Konva). World coordinates are the drawing's own units relative to the preview origin,
// y up. One transform (scale s, offset tx/ty, y flipped) maps world to screen. Lines keep a constant
// screen width at every zoom.
import Konva from "konva";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Group, Layer, Shape, Stage } from "react-konva";
import type { LayerRole, PlanPreview, ProjectModel } from "@unitly/contracts";

type Ctx = CanvasRenderingContext2D;
type Pt = [number, number];
type View = { s: number; tx: number; ty: number };

export type CanvasOverlay = {
  viewProtectionM: number | null; // depth of the clear zone in front of windows, null hides it
  wetRadiusM: number | null; // max distance from shafts, null hides it
};

const INK = "#161513";
const BLUE = "#2348FF";
const MUTED = "#8C867B";
const N600 = "#4F4B43";
const TERRACOTTA = "#E8603C";

function roleStyle(role: LayerRole | undefined): { color: string; width: number; alpha: number } {
  switch (role) {
    case "exterior_wall":
    case "load_bearing":
    case "apartment_boundary":
      return { color: INK, width: 1.4, alpha: 1 };
    case "shaft":
    case "window":
    case "entrance_door":
    case "interior_door":
      return { color: INK, width: 1, alpha: 0.9 };
    case "movable_partition":
      return { color: N600, width: 1, alpha: 0.9 };
    case "room_label":
      return { color: N600, width: 1, alpha: 0.8 };
    default:
      return { color: MUTED, width: 0.8, alpha: 0.45 };
  }
}

function bboxOf(segments: number[]): [number, number, number, number] | null {
  if (segments.length < 4) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < segments.length; i += 2) {
    const x = segments[i], y = segments[i + 1];
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  return [x0, y0, x1, y1];
}

function niceLength(target: number): number {
  const p = 10 ** Math.floor(Math.log10(target));
  for (const m of [1, 2, 5, 10]) if (m * p >= target) return m * p;
  return 10 * p;
}

export function PlanCanvas({
  preview, roles, hidden, hovered, model, overlay, scaleToM, northAngle, focusModel,
}: {
  preview: PlanPreview | null;
  roles: Record<string, LayerRole>;
  hidden: Set<string>;
  hovered: string | null;
  model: ProjectModel | null;
  overlay: CanvasOverlay;
  scaleToM: number;
  northAngle: number;
  focusModel?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState<View>({ s: 1, tx: 0, ty: 0 });
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const fitted = useRef<string>("");

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: Math.max(50, e.contentRect.width), h: Math.max(50, e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const bboxes = useMemo(() => {
    const m = new Map<string, [number, number, number, number]>();
    for (const l of preview?.layers ?? []) {
      const b = bboxOf(l.segments);
      if (b) m.set(l.name, b);
    }
    return m;
  }, [preview]);

  // Model points (meters, model frame) to world (source units relative to the preview origin).
  const m2w = useCallback(
    (p: number[]): Pt => {
      if (!model || !preview) return [p[0], p[1]];
      const k = model.frame.scale_to_m;
      return [(p[0] + model.frame.origin_offset_m[0]) / k - preview.origin[0], (p[1] + model.frame.origin_offset_m[1]) / k - preview.origin[1]];
    },
    [model, preview],
  );

  const fit = useCallback(
    (onlyModel = false) => {
      let b: [number, number, number, number] | null = null;
      if (onlyModel && model) {
        const pts = model.boundary.polygon.map(m2w);
        b = [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1]))];
      } else {
        const meaningful = [...bboxes.entries()].filter(([n]) => roles[n] && roles[n] !== "ignore").map(([, v]) => v);
        const list = meaningful.length ? meaningful : [...bboxes.values()];
        for (const v of list) b = b ? [Math.min(b[0], v[0]), Math.min(b[1], v[1]), Math.max(b[2], v[2]), Math.max(b[3], v[3])] : v;
      }
      if (!b) return;
      const pad = 48;
      const w = Math.max(1e-6, b[2] - b[0]), h = Math.max(1e-6, b[3] - b[1]);
      const s = Math.min((size.w - pad * 2) / w, (size.h - pad * 2) / h);
      setView({ s, tx: size.w / 2 - ((b[0] + b[2]) / 2) * s, ty: size.h / 2 + ((b[1] + b[3]) / 2) * s });
    },
    [bboxes, roles, model, m2w, size],
  );

  useEffect(() => {
    // refit when the drawing or model changes, or the canvas size changes a lot (rotation, layout switch)
    const key = `${preview ? preview.origin.join() : "none"}|${model ? model.metadata.created_at : ""}|${Math.round(size.w / 250)}|${Math.round(size.h / 250)}`;
    if (preview && fitted.current !== key && size.w > 60) {
      fitted.current = key;
      fit(!!(focusModel && model));
    }
  }, [preview, model, size, fit, focusModel]);

  const toScreen = (p: Pt): Pt => [p[0] * view.s + view.tx, -p[1] * view.s + view.ty];

  const zoomAt = (factor: number, cx: number, cy: number) =>
    setView((v) => {
      const s = Math.min(1e6, Math.max(1e-6, v.s * factor));
      const k = s / v.s;
      return { s, tx: cx - (cx - v.tx) * k, ty: cy - (cy - v.ty) * k };
    });

  const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const p = e.target.getStage()?.getPointerPosition();
    if (p) zoomAt(Math.exp(-e.evt.deltaY * 0.0015), p.x, p.y);
  };
  const onDown = (e: Konva.KonvaEventObject<PointerEvent>) => {
    drag.current = { x: e.evt.clientX, y: e.evt.clientY, tx: view.tx, ty: view.ty };
  };
  const onMove = (e: Konva.KonvaEventObject<PointerEvent>) => {
    const d = drag.current;
    if (d) setView((v) => ({ ...v, tx: d.tx + e.evt.clientX - d.x, ty: d.ty + e.evt.clientY - d.y }));
  };
  const onUp = () => (drag.current = null);

  const layers = (preview?.layers ?? []).filter((l) => !hidden.has(l.name));
  const dim = !!model;

  const drawSegments = (c: Ctx, segs: number[], color: string, widthPx: number, alpha: number) => {
    c.beginPath();
    for (let i = 0; i + 3 < segs.length; i += 4) {
      c.moveTo(segs[i], segs[i + 1]);
      c.lineTo(segs[i + 2], segs[i + 3]);
    }
    c.globalAlpha = alpha;
    c.strokeStyle = color;
    c.lineWidth = widthPx / view.s;
    c.stroke();
    c.globalAlpha = 1;
  };

  const native = (ctx: Konva.Context) => (ctx as unknown as { _context: Ctx })._context;

  // Scale bar: about 100 px long.
  const metersPerPx = scaleToM / view.s;
  const barM = niceLength(metersPerPx * 100);
  const barPx = barM / metersPerPx;

  const hoveredLayer = hovered ? preview?.layers.find((l) => l.name === hovered) : null;

  return (
    <div ref={box} style={{ position: "absolute", inset: 0, touchAction: "none" }}>
      <Stage width={size.w} height={size.h} onWheel={onWheel} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        style={{ cursor: drag.current ? "grabbing" : "grab" }}>
        <Layer listening={false}>
          <Group x={view.tx} y={view.ty} scaleX={view.s} scaleY={-view.s}>
            {layers.map((l) => {
              const st = roleStyle(roles[l.name]);
              return (
                <Shape key={l.name} perfectDrawEnabled={false} sceneFunc={(ctx) => {
                  drawSegments(native(ctx), l.segments, st.color, st.width, dim ? st.alpha * 0.35 : st.alpha);
                }} />
              );
            })}
          </Group>
        </Layer>

        {model && (
          <Layer listening={false}>
            <Group x={view.tx} y={view.ty} scaleX={view.s} scaleY={-view.s}>
              <Shape perfectDrawEnabled={false} sceneFunc={(ctx) => drawModel(native(ctx), model, m2w, view.s, overlay)} />
            </Group>
          </Layer>
        )}

        {hoveredLayer && !hidden.has(hoveredLayer.name) && (
          <Layer listening={false}>
            <Group x={view.tx} y={view.ty} scaleX={view.s} scaleY={-view.s}>
              <Shape perfectDrawEnabled={false} sceneFunc={(ctx) => drawSegments(native(ctx), hoveredLayer.segments, BLUE, 2, 1)} />
            </Group>
          </Layer>
        )}

        <Layer listening={false}>
          <Shape perfectDrawEnabled={false} sceneFunc={(ctx) => {
            const c = native(ctx);
            // room label texts from the drawing (only when they would be readable)
            if (!model) {
              for (const l of layers) {
                if (!l.texts.length || roles[l.name] === "ignore") continue;
                c.fillStyle = l.name === hovered ? BLUE : N600;
                for (const t of l.texts) {
                  const px = t.height * view.s;
                  if (px < 7) continue;
                  const [x, y] = toScreen([t.x, t.y]);
                  c.font = `500 ${Math.min(16, Math.max(10, px))}px Inter, sans-serif`;
                  c.fillText(t.text.split("\n")[0].slice(0, 40), x, y);
                }
              }
            } else {
              drawModelLabels(c, model, (p) => toScreen(m2w(p)), view.s);
            }
          }} />
        </Layer>
      </Stage>

      <div className="canvas-tools">
        <button title="Zoom in" aria-label="Zoom in" onClick={() => zoomAt(1.4, size.w / 2, size.h / 2)}>+</button>
        <button title="Zoom out" aria-label="Zoom out" onClick={() => zoomAt(1 / 1.4, size.w / 2, size.h / 2)}>{"−"}</button>
        <button title="Fit to drawing" aria-label="Fit to drawing" onClick={() => fit(!!model)}>{"▢"}</button>
      </div>

      <div style={{ position: "absolute", right: 16, bottom: 14, zIndex: 3, display: "flex", alignItems: "flex-end", gap: 16, pointerEvents: "none" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", fontSize: 11.5, color: "var(--n-600)" }}>
          <span>{barM >= 1 ? `${barM} m` : `${Math.round(barM * 100)} cm`}</span>
          <div style={{ width: barPx, height: 6, border: `1px solid ${INK}`, borderTop: "none", background: `linear-gradient(90deg, ${INK} 50%, transparent 50%)`, backgroundSize: `${barPx / 2}px 2px`, backgroundRepeat: "no-repeat", backgroundPosition: "0 100%" }} />
        </div>
        <svg width="40" height="40" viewBox="-20 -20 40 40" aria-label={`North, ${northAngle} degrees`} style={{ transform: `rotate(${northAngle}deg)` }}>
          <circle r="18" fill="#FFFFFF" stroke={INK} strokeWidth="1" />
          <path d="M0 -8 L6 11 L0 7 L-6 11 Z" fill={INK} />
          <text y="-9.5" textAnchor="middle" fontSize="8" fontWeight="600" fill={INK} style={{ fontFamily: "Inter" }}>N</text>
        </svg>
      </div>
    </div>
  );
}

function polygonPath(c: Ctx, pts: Pt[]) {
  c.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
  c.closePath();
}

function hatch(c: Ctx, pts: Pt[], spacingPx: number, s: number, color: string, alpha: number) {
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const step = spacingPx / s;
  c.save();
  c.beginPath();
  polygonPath(c, pts);
  c.clip();
  c.beginPath();
  for (let d = x0 - (y1 - y0); d < x1; d += step) {
    c.moveTo(d, y0);
    c.lineTo(d + (y1 - y0), y1);
  }
  c.globalAlpha = alpha;
  c.strokeStyle = color;
  c.lineWidth = 1 / s;
  c.stroke();
  c.restore();
  c.globalAlpha = 1;
}

function drawModel(c: Ctx, m: ProjectModel, m2w: (p: number[]) => Pt, s: number, overlay: CanvasOverlay) {
  const k = 1 / m.frame.scale_to_m; // meters to drawing units
  const boundary = m.boundary.polygon.map(m2w);

  c.beginPath();
  polygonPath(c, boundary);
  c.fillStyle = "rgba(35,72,255,0.05)";
  c.fill();

  // view protection zones in front of every window, clipped to the flat
  if (overlay.viewProtectionM) {
    for (const w of m.openings.windows) {
      const [a, b] = w.segment;
      const n = w.normal;
      const d = overlay.viewProtectionM;
      const zone: Pt[] = [m2w(a), m2w(b), m2w([b[0] - n[0] * d, b[1] - n[1] * d]), m2w([a[0] - n[0] * d, a[1] - n[1] * d])];
      c.save();
      c.beginPath();
      polygonPath(c, boundary);
      c.clip();
      hatch(c, zone, 7, s, BLUE, 0.55);
      c.beginPath();
      polygonPath(c, zone);
      c.setLineDash([5 / s, 4 / s]);
      c.strokeStyle = BLUE;
      c.lineWidth = 1 / s;
      c.stroke();
      c.setLineDash([]);
      c.restore();
    }
  }

  // wet room reach around shafts
  if (overlay.wetRadiusM) {
    for (const sh of m.fixed.shafts) {
      const [x, y] = m2w(sh.centroid);
      c.beginPath();
      c.arc(x, y, overlay.wetRadiusM * k, 0, Math.PI * 2);
      c.setLineDash([3 / s, 4 / s]);
      c.strokeStyle = N600;
      c.globalAlpha = 0.7;
      c.lineWidth = 1 / s;
      c.stroke();
      c.setLineDash([]);
      c.globalAlpha = 1;
    }
  }

  // walls, columns: solid poche
  for (const e of [...m.fixed.exterior_walls, ...m.fixed.load_bearing]) {
    c.beginPath();
    polygonPath(c, e.polygon.map(m2w));
    c.fillStyle = INK;
    c.globalAlpha = e.kind === "exterior_wall" ? 0.82 : 1;
    c.fill("evenodd");
    c.globalAlpha = 1;
  }
  // shafts: hatched
  for (const sh of m.fixed.shafts) {
    const pts = sh.polygon.map(m2w);
    c.beginPath();
    polygonPath(c, pts);
    c.fillStyle = "#FFFFFF";
    c.fill();
    hatch(c, pts, 4, s, INK, 0.9);
    c.beginPath();
    polygonPath(c, pts);
    c.strokeStyle = INK;
    c.lineWidth = 1.2 / s;
    c.stroke();
  }
  // existing partitions: dashed, they may move
  c.beginPath();
  for (const p of m.existing.partitions) {
    const pts = p.polyline.map(m2w);
    c.moveTo(pts[0][0], pts[0][1]);
    for (const q of pts.slice(1)) c.lineTo(q[0], q[1]);
  }
  c.setLineDash([6 / s, 4 / s]);
  c.strokeStyle = N600;
  c.lineWidth = 1.2 / s;
  c.stroke();
  c.setLineDash([]);

  // boundary
  c.beginPath();
  polygonPath(c, boundary);
  c.strokeStyle = BLUE;
  c.lineWidth = 2 / s;
  c.stroke();

  // windows
  c.beginPath();
  for (const w of m.openings.windows) {
    const [a, b] = w.segment.map(m2w);
    c.moveTo(a[0], a[1]);
    c.lineTo(b[0], b[1]);
  }
  c.strokeStyle = BLUE;
  c.lineWidth = 5 / s;
  c.lineCap = "butt";
  c.stroke();

  // entrance: a small terracotta unit at the door
  const e = m.openings.entrance;
  if (e) {
    const [a, b] = e.segment.map(m2w);
    c.beginPath();
    c.moveTo(a[0], a[1]);
    c.lineTo(b[0], b[1]);
    c.strokeStyle = TERRACOTTA;
    c.lineWidth = 5 / s;
    c.stroke();
  }
  // interior doors: thin ink marks
  c.beginPath();
  for (const d of m.openings.interior_doors) {
    const [a, b] = d.segment.map(m2w);
    c.moveTo(a[0], a[1]);
    c.lineTo(b[0], b[1]);
  }
  c.strokeStyle = INK;
  c.lineWidth = 2 / s;
  c.stroke();
}

function drawModelLabels(c: Ctx, m: ProjectModel, toScreen: (p: number[]) => Pt, s: number) {
  const tag = (x: number, y: number, text: string, bg: string, fg = "#FFFFFF") => {
    c.font = "600 11px Inter, sans-serif";
    const w = c.measureText(text).width + 8;
    c.fillStyle = bg;
    c.fillRect(x - w / 2, y - 8, w, 16);
    c.fillStyle = fg;
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText(text, x, y + 0.5);
    c.textAlign = "start";
    c.textBaseline = "alphabetic";
  };
  for (const w of m.openings.windows) {
    const off = 0.55; // meters outward
    const [x, y] = toScreen([w.center[0] + w.normal[0] * off, w.center[1] + w.normal[1] * off]);
    tag(x, y, w.id, BLUE);
  }
  const e = m.openings.entrance;
  if (e && e.inward_normal) {
    const [x, y] = toScreen([e.center[0] - e.inward_normal[0] * 0.55, e.center[1] - e.inward_normal[1] * 0.55]);
    tag(x, y, e.id, TERRACOTTA);
  }
  if (s > 0) {
    c.fillStyle = N600;
    c.font = "500 11.5px Inter, sans-serif";
    for (const l of m.existing.room_labels) {
      const [x, y] = toScreen(l.position);
      c.fillText(l.text.split("  ")[0].slice(0, 32), x, y);
    }
    for (const sh of m.fixed.shafts) {
      const [x, y] = toScreen(sh.centroid);
      tag(x, y - 14, sh.id, INK);
    }
  }
}
